/**
 * useHeroBox — PROD-1431 (RR1431 M1). The four tall navy heroes (ViewSummary, CaseTimeline,
 * CareAgreement, the ScheduleFollowup booking step) are two siblings: a pinned back-arrow row and a
 * body that scrolls away. Both paint slices of ONE gradient box, so at rest the hero reads as one
 * piece. That box must be exactly as tall as the hero: a fixed box ends inside a taller hero (a meta
 * line that wraps, a longer translation) and draws a hard line where the flat end colour starts.
 *
 * This hook measures row + body with a ResizeObserver and writes the sum to the CSS variable
 * `--hero-h` on their common parent (the page root). Both slices read
 * `100% var(--hero-h, <HERO_H>)`, so before the first measure (and where ResizeObserver is missing)
 * the box is the fixture height it was before.
 *
 * Usage: `const [heroRow, heroBody] = useHeroBox()`, then `ref={heroRow}` on the pinned row and
 * `ref={heroBody}` on the body right after it. check-sticky-header I1 holds the wiring.
 */
import { useLayoutEffect, useState } from 'react'

export function useHeroBox() {
  // callback refs kept in state: a hero that mounts after a loading branch is still measured
  const [row, setRow] = useState(null)
  const [body, setBody] = useState(null)
  useLayoutEffect(() => {
    if (!row || !body) return undefined
    const host = row.parentElement
    if (!host || body.parentElement !== host) return undefined
    const write = () => {
      const h = row.getBoundingClientRect().height + body.getBoundingClientRect().height
      // a zero (not laid out, or a test DOM) keeps the fallback box. Rounded UP to a whole px: the
      // hero's background is painted to the next device pixel, and a box a fraction short leaves its
      // last pixel row in the flat end colour (a 12-14 step at the bottom edge, measured at DPR 3).
      if (h > 0) host.style.setProperty('--hero-h', `${Math.ceil(h - 0.01)}px`)
    }
    write()
    if (typeof ResizeObserver === 'undefined') return () => host.style.removeProperty('--hero-h')
    const ro = new ResizeObserver(write)
    ro.observe(row)
    ro.observe(body)
    return () => {
      ro.disconnect()
      host.style.removeProperty('--hero-h')
    }
  }, [row, body])
  return [setRow, setBody]
}

export default useHeroBox
